/**
 * Page Findings : liste triable/filtrable/recherchable/exportable des
 * observations de sécurité. Les Findings High et CIS France sont mis en
 * évidence automatiquement.
 */
import { useEffect, useState } from "react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import useQuickFilter from "@/hooks/useQuickFilter";
import DataTable from "@/components/tables/DataTable";
import SummaryChip from "@/components/tables/SummaryChip";
import IdLinkCell from "@/components/tables/IdLinkCell";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PAGES, QUICK_FILTER_KPI } from "@/constants/testIds";

const SEVERITY_STYLE = {
  High: "bg-red-100 text-red-700",
  Medium: "bg-amber-100 text-amber-700",
  Low: "bg-slate-100 text-slate-600",
};

const COLUMNS = [
  { key: "id", label: "Numéro" },
  { key: "date", label: "Date" },
  { key: "site", label: "Site" },
  { key: "declarant_name", label: "Déclarant" },
  { key: "category", label: "Catégorie" },
  { key: "severity", label: "Sévérité" },
  { key: "status", label: "Statut" },
  { key: "late", label: "Retard" },
  { key: "cis_france", label: "CIS France" },
];

const QUICK_FILTERS = {
  high: (r) => r.severity === "High",
  open: (r) => r.status === "Open",
  late: (r) => r.late,
  cis: (r) => r.cis_france,
};

export default function FindingsPage() {
  const { filters, refreshKey } = useFilters();
  const [data, setData] = useState(null);
  const { active: quickFilter, toggle: toggleQuickFilter, clear: clearQuickFilter, apply: applyQuickFilter } = useQuickFilter(QUICK_FILTERS);

  useEffect(() => {
    dashboardApi.getFindings(filters).then(({ data }) => setData(data));
  }, [filters, refreshKey]);

  const renderCell = (col, row) => {
    if (col.key === "id") {
      return <IdLinkCell id={row.id} href={row.link} testId={`findings-link-${row.id}`} />;
    }
    if (col.key === "severity") {
      return <Badge className={`border-none ${SEVERITY_STYLE[row.severity]}`}>{row.severity}</Badge>;
    }
    if (col.key === "status") {
      return (
        <Badge variant="outline" className={row.status === "Open" ? "border-amber-300 text-amber-700" : "border-emerald-300 text-emerald-700"}>
          {row.status}
        </Badge>
      );
    }
    if (col.key === "late") {
      return row.late ? <Badge className="border-none bg-red-100 text-red-700">En retard</Badge> : <span className="text-slate-400">-</span>;
    }
    if (col.key === "cis_france") {
      return row.cis_france ? (
        <Badge className="border-none bg-[#0B5394]/10 text-[#0B5394]">CIS France</Badge>
      ) : (
        <span className="text-slate-400">-</span>
      );
    }
    return undefined;
  };

  if (!data) return <Skeleton className="h-96 rounded-lg" />;

  return (
    <div data-testid={PAGES.findings} className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">Findings</h1>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryChip testId={QUICK_FILTER_KPI.findingsTotal} label="Total" value={data.summary.total} onClick={clearQuickFilter} active={!quickFilter} />
        <SummaryChip testId={QUICK_FILTER_KPI.findingsHigh} label="High" value={data.summary.high} tone="red" onClick={() => toggleQuickFilter("high")} active={quickFilter === "high"} />
        <SummaryChip testId={QUICK_FILTER_KPI.findingsOpen} label="Ouverts" value={data.summary.open} tone="blue" onClick={() => toggleQuickFilter("open")} active={quickFilter === "open"} />
        <SummaryChip testId={QUICK_FILTER_KPI.findingsLate} label="En retard" value={data.summary.late} tone="orange" onClick={() => toggleQuickFilter("late")} active={quickFilter === "late"} />
      </div>
      <DataTable testId="findings-table" title="Findings" columns={COLUMNS} data={applyQuickFilter(data.items)} renderCell={renderCell} />
    </div>
  );
}
